import type { EntityId, EntityState } from "@reduxjs/toolkit"

import * as Geo from "@/base/geo"
import i18n from "@/translations/i18n"
import { createEntityAdapter } from "@reduxjs/toolkit"
import * as turf from "@turf/turf"

import { logger } from "../base/logger"
import { isNumberValid, isTruthyString } from "../base/type-guards"

// import type { Dictionary, EntityId } from "@reduxjs/toolkit"

import type { ChartData, FetchStatus } from "@/base/types"
import type { PivotDirection } from "@/constants/base"
import type {
  DevicePermissions,
  FarmUserPermissions,
} from "@/constants/permissions"
import type {
  DeviceFunctionName,
  RelayAction,
  RelayActionType,
} from "@/redux/device-commands.reducer"
import type { CenterPivotPayload } from "@/redux/center-pivots.reducer"
import type { LinearIrrigatorPayload } from "@/redux/linear-irrigators.reducer"
import type { PivotEndGunTable } from "@/redux/pivot-end-gun-tables.reducer"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import type { IrrigationPlan } from "@/redux/irrigation-plans.reducer"
import type { FieldType } from "@/redux/farm-fields.reducer"

import type { ReelRunErrorCode } from "@/redux/reel-runs.reducer"
import type { RootState } from "@/redux/root.reducer"
import type { AppIcons } from "@/theme"
import type { SupportedAppLanguageCode } from "@/translations/i18n"

import type { DeviceCommentStatus } from "../base/farmhq-api"
import type { AnyRequestName } from "../base/send-request"
import type {
  ApplicationType,
  ConfigSensorName,
  HardwareGeneration,
  InstallationType,
  ParticlePlatform,
  ParticleProductId,
  SensorName,
  SprinklerType,
} from "../base/sensor-configurations"
import type {
  SensorEventKey,
  SensorEvents,
  SensorState,
} from "../base/sensor-events"
import type { ReelRunHistorical } from "@/redux/field-irrigation-history.reducer"

export interface DeviceIdProps {
  deviceId: string
}
export interface TriggerIdProps {
  triggerId: number
}
export interface CodaDeviceAliasProps {
  codaDeviceAlias: string
}
export interface FieldIdProps {
  fieldId: number
}

export interface ReelRunIdProps {
  reelRunId: number
}

export interface TrackedRequest<Id extends AnyRequestName = AnyRequestName> {
  fetchStatus?: FetchStatus
  id: Id
  numberOfTries?: number
}

export interface ModelState<T, I extends EntityId = number> {
  entities: Record<I, T>
  ids: I[]
}
export function getInitialEntityState<T, I extends EntityId>(): EntityState<
  T,
  I
> {
  return {
    entities: {} as Record<I, T>,
    ids: [],
  }
}

function createModel<T extends object, I extends EntityId = number>({
  getState,
  options,
  selectId,
}: {
  getState: (state: RootState) => EntityState<T, I>
  options?: {
    enhancer?: (instance: T) => T
    sortComparer?: (
      a: Partial<T> | undefined,
      b: Partial<T> | undefined,
    ) => number
  }
  selectId: (model: T) => I
}) {
  const adapter = createEntityAdapter<T, I>({
    selectId,
    sortComparer: options?.sortComparer,
  })

  const selectors = adapter.getSelectors(getState)
  return {
    adapter,
    ...selectors,
    selectIds: selectors.selectIds as (state: RootState) => I[],
  }
}

export type NotificationChannelPreference = "sms" | "push" | "both" | "none"

export interface UserAccountData {
  isAdmin: boolean | null
  nameFamily?: string | null
  nameGiven?: string | null
  notificationChannelPreference?: NotificationChannelPreference
  preferredLanguageCodeOverride: SupportedAppLanguageCode | null
  termsOfServiceAccepted: boolean | null
}

export interface NotificationEntity {
  content: string
  id: number
  imageDescription?: string | null
  imageUri?: string | null
  publishAtTimestamp: string
  readStatus: "not_seen" | "seen" | null
  title: string
}

/**
 * User farm accounts
 */
export const userFarm = createModel<FarmAccount>({
  getState: (state) => state.userFarms,
  options: {
    sortComparer: (a, b) => {
      const nameA = a?.name ?? ""
      const nameB = b?.name ?? ""
      return nameA.localeCompare(nameB)
    },
  },
  selectId: (farm) => farm.id,
})

// export interface AppFeatureEntity {
//   featureName: AppFeedbackKey
//   feedbackText?: string | null
//   isLiked?: boolean
// }

// /**
//  * App features with feedback ratings
//  */
// export const appFeature = createModel<AppFeatureEntity, AppFeedbackKey>({
//   getState: (state) => state.appFeedback,
//   selectId: (entity) => entity.featureName,
// })

/**
 * Device Configurations
 */
export const deviceConfiguration = createModel<DeviceConfiguration, string>({
  getState: (state) => state.deviceConfigurations,
  options: {
    sortComparer: (a, b) => {
      const nameA = a?.deviceName ?? a?.codaDeviceAlias ?? ""
      const nameB = b?.deviceName ?? b?.codaDeviceAlias ?? ""
      return nameA.localeCompare(nameB)
    },
  },
  selectId: (dc) => dc.deviceId,
})
export type FarmJoinCodeStatus = "ACTIVE" | "EXPIRED" | "NOT_CREATED"
export interface FarmJoinCode {
  codeStatus: FarmJoinCodeStatus
  codeStr: string
  createDate: string
  createdByUserId?: string
  expirationDate: string
  id: number | null
  permissionExpirationDate?: string | null | undefined
  permissions: FarmUserPermissions
}

export interface DeviceFarmAssociation {
  createDate?: string
  createdByUserId?: string
  device: string | undefined
  deviceId: string | undefined
  expirationDate: string
  expiredByUserId?: string | null
  farmId: number
  farmName: string
  id: number
  isPrimaryOwner: boolean
  permissions: DevicePermissions
}

export interface DeviceShareCode {
  codeStr: string
  deviceId: string
  expirationDate: string
  farmName: string
  id: number
  ownerFarmId: number
  permissionExpirationDate: string
  permissions: DevicePermissions
}

/**
 * Facilitate device sharing
 * TODO: We don't really use these currently
 */
export const deviceShareCode = createModel<DeviceShareCode, string>({
  getState: (state) => state.deviceShareCodes,
  selectId: (code) => code.deviceId,
})
export interface DeviceUserAssociation {
  deviceId: string
  id?: number
  notificationsSuppressed: boolean
  userId?: string
}
/**
 * Connect devices to users
 *
 * Currently, we only use these for toggling notification status for a user,
 * i.e. blocking notifications to a user's phone number
 */
export const deviceUserAssociation = createModel<
  DeviceUserAssociation,
  string
>({
  getState: (state) => state.deviceUserAssociations,
  selectId: (dua) => dua.deviceId,
})

/**
 * Device Action Schedules are foreign-keyed to crons
 */
export interface CronSchedule {
  farmId?: number | null
  id: number
  raw: string
  timezone?: string | null
}

export interface ScheduledDeviceAction {
  command: string
  deviceActionScheduleId?: number | null
  deviceConfigurationId?: number
  /**
   * NOTE: This is seconds
   */
  executeAtTs: number
  executedInDeviceEvent?: number | null
  id: number
  isActive?: boolean | null
  isExecuted: boolean
  isScheduled?: boolean
  namedDeviceActionId: number | null
}

export type ObservableDeviceState = "dry" | "unknown" | "wet"

export interface NamedDeviceAction {
  actionType: string | null
  arguments: Record<string, boolean | number | string | null | undefined> | null
  device: string | null
  displayName: string | null
  expectedOutcome: ObservableDeviceState | null
  hardwareGeneration: HardwareGeneration | null
  iconKey?: keyof typeof AppIcons
  id: number
  installationType: InstallationType | null
  name: string | null
  sensorId: number | null
  sensorName: SensorName | null
  sensorType: string
}
// Re-exported from a dedicated module so tests can import the enum
// + translator without dragging the whole redux model surface (and
// transitive ``@reduxjs/toolkit`` ESM) through Jest's transform
// pipeline.
export {
  DeviceReturnCode,
  translateDeviceReturnCode,
} from "./device-return-code"

export interface DeviceFunctionCall {
  argumentString: string | null | undefined
  deviceId: string | null | undefined
  deviceReturnCode: DeviceReturnCode | null
  email: string | null | undefined
  functionName: DeviceFunctionName | null | undefined
  httpStatusCode: number | null | undefined
  id: number
  initiatedByTriggerId: number | null | undefined
  initiatedByTriggerSourceDeviceName: string | null | undefined
  initiatedByUser: string | null | undefined
  namedDeviceAction: NamedDeviceAction | null | undefined
  relayAction: RelayAction | null | undefined
  requestSentTimestamp: string | null | undefined
  responseReceivedTimestamp: string | null | undefined
}

export type ScheduleSyncStatus = "FAILED" | "IN_PROGRESS" | "SUCCESS"
export interface DeviceActionSchedule {
  createDate: string | null
  createdByUserId?: string | null
  cronSchedule: CronSchedule | null | undefined
  deactivateDate: string | null
  deviceId: string
  executeAtTs: string | null | undefined
  id: number
  isActive: boolean | null
  isEnabled?: boolean | null
  isSynced: boolean | null
  namedDeviceAction: NamedDeviceAction | null
  namedDeviceActionId: number
  scheduleEnd: string | null | undefined
  scheduleStart: string | null | undefined
  syncStatus?: ScheduleSyncStatus | null
}

export const deviceActionSchedules = createModel<DeviceActionSchedule>({
  getState: (state) => state.deviceActionSchedules,
  selectId: (model) => model.id,
})
export interface DeviceEvent extends SensorEvents {
  deviceEventTimestamp: string
  deviceId: string
  devicePlatform: ParticlePlatform | null
  deviceProductId: ParticleProductId | null
  firmwareVersion: number
  id: number
  particleEventTimestamp: string
  scheduledDeviceActions: ScheduledDeviceAction[]
}

/**
 * Slim record returned by LoadFarmTimelineEvents — one per state transition
 * across all devices on the active farm. Each row already carries the prior
 * value of the changed column in its `*Previous` field (written by the
 * ingest pipeline), so the frontend can identify the transition without
 * comparing adjacent rows.
 */
export interface FarmTimelineEvent {
  id: number
  deviceId: string
  timestamp: string
  pressureStateCurrent: string | null
  pressureStatePrevious: string | null
  reelStateCurrent: string | null
  reelStatePrevious: string | null
  relayStateCurrent: string | null
  relayStatePrevious: string | null
}

/**
 * Most recent event for each device, if any
 */
export const deviceEventLast = createModel<DeviceEvent, string>({
  getState: (state) => state.deviceEventLast,
  options: {
    sortComparer: (a, b) => {
      return (a?.deviceEventTimestamp ?? "").localeCompare(
        b?.deviceEventTimestamp ?? "",
      )
    },
  },
  selectId: (event) => event.deviceId,
})

export interface DeviceTimeseriesData<
  S extends SensorName,
  K extends SensorEventKey<S>,
> extends ChartData {
  key: K
  sensorName: S
}

export type DeviceTimeSeriesUnion =
  | DeviceTimeseriesData<"battery", "internalSoc">
  | DeviceTimeseriesData<"battery", "voltageMv">
  | DeviceTimeseriesData<"flow", "rateLpmAvg">
  | DeviceTimeseriesData<"flow", "volumeLTotal">
  | DeviceTimeseriesData<"pressure", "readingKpa">

/**
 * Date for device profile timeseries key
 */
export const deviceTimeseries = createModel<
  DeviceTimeSeriesUnion,
  DeviceTimeSeriesUnion["key"]
>({
  getState: (state) => state.deviceProfile.timeseries,
  selectId: (model) => model.key,
})

export interface OtherFarmUser {
  associationId: number
  email: string
  isAdmin: boolean
  isFake: boolean
  userId: string
}

export interface FieldSvgData {
  points: string | undefined
  viewbox: string | undefined
}
export interface FieldDatabaseValue {
  center: Geo.PointGeoJson | undefined
  createDate?: string | null
  createdByUserId?: string | null
  farmId: number
  fieldName: string
  fieldType: FieldType | null | undefined
  fillColor?: string
  id: number
  isActive: boolean
  labelRotationDegrees?: number | null
  polygon: Geo.PolygonGeoJson
  rowDirectionAzimuthDegrees: number | null
}
export interface FarmField extends FieldDatabaseValue {
  areaAcres: number | undefined
  areaText: string | undefined
  centerLatLng: Geo.MultiPoint | null | undefined
  fieldId: number
  fieldType: FieldType
  labelRotationDegrees: number
  path: Geo.MultiPath | undefined
  rowDirectionAzimuthDegrees: number
  svgInfo: FieldSvgData
}

export function calculateFieldSvgPath(
  f: { polygon: Geo.PolygonGeoJson } | undefined,
): FieldSvgData {
  let points: string | undefined
  let viewbox: string | undefined
  const polygon = Geo.polygon(f?.polygon.coordinates[0])
  if (polygon) {
    const boundingBox = polygon.addBuffer(200)?.getBoundingBox()
    if (boundingBox) {
      const boundingBoxHeight = boundingBox.north - boundingBox.south
      const boundingBoxWidth = boundingBox.east - boundingBox.west
      viewbox = `0 0 ${boundingBoxWidth} ${boundingBoxHeight}`
      points = ""
      for (const [lng, lat] of polygon.getOuterRing()) {
        if (isNumberValid(lng) && isNumberValid(lat)) {
          const distanceFromNorth = boundingBox.north - lat
          const distanceFromWest = lng - boundingBox.west
          points = `${points} ${distanceFromWest}, ${distanceFromNorth}`
        }
      }
    }
  }
  return { points, viewbox }
}
export function calculateFieldArea(polygon: Geo.PolygonGeoJson) {
  try {
    const cleaned = Geo.polygon(polygon)?.toJson().coordinates
    if (cleaned) {
      const areaMetersSquare = turf.area(turf.polygon(cleaned))
      return Geo.round(
        turf.convertArea(areaMetersSquare, "meters", "acres"),
        1,
      )
    }
  } catch (error) {
    logger.error(error)
  }
  return undefined
}

/**
 * Cleans the field and adds extra properties
 */
export function prepareField(fieldData: FieldDatabaseValue): FarmField {
  const {
    center,
    fieldType,
    labelRotationDegrees,
    polygon,
    rowDirectionAzimuthDegrees,
  } = fieldData

  const polygonWrapper = Geo.polygon(polygon)
  const centerPoint = center ?? polygonWrapper?.getCenter()
  const [centerLng, centerLat] = centerPoint?.coordinates ?? []
  const areaAcres = calculateFieldArea(polygon)

  const svgInfo = calculateFieldSvgPath({ polygon })

  // calculate native and web centers
  let centerLatLng: FarmField["centerLatLng"] | undefined
  if (isNumberValid(centerLng) && isNumberValid(centerLat)) {
    centerLatLng = {
      native: { latitude: centerLat, longitude: centerLng },
      web: { lat: centerLat, lng: centerLng },
    }
  }

  // calculate native and web paths
  let path: FarmField["path"] | undefined
  const pathGoogle = polygonWrapper?.toGmaps()
  const pathNative = polygonWrapper?.toNative()

  if (pathGoogle && pathNative) {
    path = {
      native: pathNative,
      web: pathGoogle,
    }
  }
  // Try to close the polygon if necessary and if possible
  const polygonCleaned = polygonWrapper?.toJson() ?? fieldData.polygon
  return {
    ...fieldData,
    areaAcres,
    areaText: isNumberValid(areaAcres)
      ? `${areaAcres} ${i18n.t("acres", { ns: "common" })}`
      : undefined,
    center: Geo.point(centerPoint?.coordinates)?.toJson(),
    centerLatLng,
    fieldId: fieldData.id,
    fieldType: fieldType ?? "polygon",
    labelRotationDegrees: labelRotationDegrees ?? 0,
    path,
    polygon: polygonCleaned,
    rowDirectionAzimuthDegrees: rowDirectionAzimuthDegrees ?? 0,
    svgInfo,
  }
}

/**
 * Field polygons for farm
 */
export const farmField = createModel<FarmField>({
  getState: (state) => state.farmFields,
  options: {
    sortComparer: (a, b) => {
      const nameA = a?.fieldName ?? ""
      const nameB = b?.fieldName ?? ""
      return nameA.localeCompare(nameB)
    },
  },
  selectId: (model) => model.id,
})
export interface EventActionTriggerIds {
  sourceDeviceId: string
  targetDeviceId: string | null
}

export const namedDeviceAction = createModel<NamedDeviceAction>({
  getState: (state) => state.namedDeviceActions,
  selectId: (model) => model.id,
})

export type EventActionTriggerProperties<
  SourceSensor extends SensorName = SensorName,
> = EventActionTriggerIds & {
  namedDeviceAction: NamedDeviceAction | null
  notificationString?: string | null
  notify: boolean | null
  sourceSensor: SourceSensor
  sourceSensorStateCurrent: SensorState<SourceSensor>
  sourceSensorStatePrevious: SensorState<SourceSensor>
}

export type EventActionTrigger<S extends ConfigSensorName = ConfigSensorName> =
  EventActionTriggerProperties<S> & {
    createDate?: string
    delaySeconds?: number | null
    expirationDate?: string
    expiredByUserId?: string
    id: number
  }
export type DevicePair = EventActionTriggerProperties<"reel" | "wheel">

/**
 * Event action triggers, including connections and notifications
 */
export const deviceConnection = createModel<EventActionTrigger>({
  getState: (state) => state.deviceConnections,
  selectId: (t) => t.id,
})

export interface PhoneNumberDigits {
  areaCode: string
  countryCode: string
  prefix: string
  suffix: string
}

export interface PhoneNumber {
  codeExpirationDate: string | null
  codeString: string | null
  digits: PhoneNumberDigits | { [K in keyof PhoneNumberDigits]: null }
  e164: string
  id: number
  isVerified: boolean
  notificationsEnabled: boolean
  serialized: string
  userId: string
}

/**
 * Registered phone number for user
 */
export const phoneNumber = createModel<PhoneNumber>({
  getState: (state) => state.userPhoneNumbers,
  selectId: (number) => number.id,
})
interface ReelRunBase {
  deviceConfigurationId: number | null
  deviceEventTimestamps: Array<string | null>
  deviceId: string | null
  directionOverrideAzimuthDegrees: number | null
  distanceReportsMm: Array<number | null>
  endPoint: Geo.PointGeoJson | null
  endTimestamp: string | null
  inProgress: boolean
  lastEventTimestamp: string | null
  pressureReportsKpa?: Array<number | null>
  reelRunId: number
  speedReportsMmpm: Array<number | null>
  startTimestamp: string
}
export type CalculationMethod =
  | "flow_rate_override"
  | "nozzle_diameter_current"
  | "nozzle_diameter_historical"

export interface ReelRun extends ReelRunBase {
  applicationRateReportsMm: Array<number | null> | null | undefined
  /**
   * Resolved by the backend view: per-run override wins, else the reel
   * configuration's application type
   */
  applicationType?: ApplicationType | null
  /**
   * The manual field pin, if any. `fieldId` is the RESOLVED field: this
   * pin when set, else geometric (GPS end point) attribution
   */
  fieldOverrideId?: number | null
  calculationMethod?: CalculationMethod | null
  cartLocationPoint: number | null
  completionPct: number | null
  deviceConfigurationId: number | null
  distanceMmCurrent: number | null | undefined
  distanceMmMax: number
  duration: number | null
  // TODO: DELETE
  errorCode: ReelRunErrorCode | null
  extendHeadingDegrees: number | null
  farmId: number
  fieldId: number | null
  flowRateReportsLpm?: Array<number | null> | null | undefined
  reelFlowRateOverrideLpm?: number | null
  reelNNozzles: number | null
  reelNozzleDiameterMm: number | null
  reelSprinklerType: SprinklerType | null
  reelSwathWidthMm: number | null
  startPoint: Geo.PointGeoJson | null
}

/**
 * Put reel runs in order
 * @param a {ReelRun}
 * @param b {ReelRun}
 * @returns number
 */
function sortReelRuns(
  a: Partial<ReelRun> | undefined,
  b: Partial<ReelRun> | undefined,
): number {
  const timestampA = a?.lastEventTimestamp
  const timestampB = b?.lastEventTimestamp
  if (isTruthyString(timestampA) && isTruthyString(timestampB)) {
    return timestampB.localeCompare(timestampA)
  }
  return 0
}

export interface IrrigationHeatmapCell {
  lat: number
  lng: number
  accumulatedMm: number
}

/** Geographic anchor of a server-rendered heatmap raster (WGS-84 degrees). */
export interface HeatmapBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface IrrigationHeatmapGrid {
  cells: IrrigationHeatmapCell[]
  maxAccumulatedMm: number
  resolutionM: number
  runCount: number
  /**
   * Server-rendered PNG of the completed-runs heatmap. Preferred over
   * `cells` when present — one map overlay instead of hundreds of
   * polygons. Null/absent until the backend raster pipeline has data.
   */
  rasterUrl?: string | null
  bounds?: HeatmapBounds | null
  rev?: string | null
}

export interface PivotHeatmapSector {
  startDegrees: number
  endDegrees: number
  accumulatedMm: number
}

export interface PivotIrrigationHeatmapGrid {
  centerLat: number
  centerLng: number
  outerRadiusM: number
  sectors: PivotHeatmapSector[]
  runCount: number
  maxAccumulatedMm: number
  /** See IrrigationHeatmapGrid.rasterUrl. */
  rasterUrl?: string | null
  bounds?: HeatmapBounds | null
  rev?: string | null
}

/**
 * Reel runs that are in progress
 */
export const reelRunsActive = createModel<ReelRun>({
  getState: (state) => state.reelRunsActive,
  options: { sortComparer: sortReelRuns },
  selectId: (run) => run.reelRunId,
})

// /**
//  *
//  */
// export const deviceProfileReelRuns = createModel<ReelRun>({
//   getState: (state) => state.deviceProfile.reelRuns,
//   options: { sortComparer: sortReelRuns },
//   selectId: (run) => run.reelRunId
// })

/**
 * Reel runs for selected field
 */
export const fieldIrrigationHistoryRuns = createModel<ReelRunHistorical>({
  getState: (state) => state.fieldIrrigationHistory,
  options: { sortComparer: sortReelRuns },
  selectId: (run) => run.reelRunId,
})

/**
 * Global app notifications
 */
export const appNotifications = createModel<NotificationEntity>({
  getState: (state) => state.appNotifications,
  options: {
    sortComparer: (a, b) => {
      const timestampA = a?.publishAtTimestamp
      const timestampB = b?.publishAtTimestamp
      if (typeof timestampA === "string" && typeof timestampB === "string") {
        const dateA = new Date(timestampA).getTime()
        const dataB = new Date(timestampB).getTime()
        return dataB - dateA
      }

      return 0
    },
  },
  selectId: (entity) => entity.id,
})

/**
 * Track requests to backend api
 */
export const requests = createModel<TrackedRequest, string>({
  getState: (state) => state.requests,
  selectId: (request) => request.id,
})

export interface DeviceCommandTracker {
  actionId?: number
  actionType?: RelayActionType
  deviceId: string
  forceUpdateCount?: number
}

/**
 * Track device command invocations
 */
export const deviceCommandTracker = createModel<DeviceCommandTracker, string>({
  getState: (state) => state.deviceCommands,
  options: {
    sortComparer: (a, b) => {
      const countA = a?.forceUpdateCount ?? 0
      const countB = b?.forceUpdateCount ?? 0
      const idA = a?.deviceId ?? ""
      const idB = b?.deviceId ?? ""
      if (isNumberValid(countA) && isNumberValid(countB)) {
        return countB - countA
      }
      return idA.localeCompare(idB)
    },
  },
  selectId: (a) => a.deviceId,
})
export interface DeviceComment {
  contentText: string | null
  createDate: string
  createdByUser: {
    email: string | null
    nameFamily: string
    nameGiven: string
  }
  device: string
  id: number
  status: DeviceCommentStatus | null
}
export const deviceComment = createModel<DeviceComment, string>({
  getState: (state) => state.deviceComments,
  selectId: (model) => model.device,
})
type ActivityArray<
  T extends number | string | null | undefined =
    | number
    | string
    | null
    | undefined,
> = T[] | null | undefined

export type ObservableIrrigationState = "dry" | "wet"

export interface DeviceActivity {
  createDate: string
  createdAtTimestamps: Array<string | null | undefined> | null | undefined
  /**
   * Note: this is the device id
   */
  device: string
  finishDate: string | null | undefined
  finishReason: string | null | undefined
  flowRateLpmAvgs: ActivityArray
  gpsHeadingsDegrees: ActivityArray
  gpsLocations: Array<Geo.PointGeoJson | null | undefined> | null | undefined
  gpsSpeedMetersPerHour: ActivityArray
  id: number
  isMarkedFinished: boolean | null | undefined
  lastCreatedAtTimestamp: string | null | undefined
  linearDistancesMeters: ActivityArray
  observableStates: Array<ObservableIrrigationState | null | undefined>
  pivotHeadingsDegrees: ActivityArray<number | null>
  pivotSpeedsHoursPerRevolution: ActivityArray
  pressureReadingKpas: ActivityArray
  reelRunDistanceMmCurrents: ActivityArray
  reelRunDistanceMmMax: number | null | undefined
  trace: string
  travelerDirections?: Array<PivotDirection | null>
  travelerDistancesMetersToNextStop: ActivityArray
  travelerNextStopExpectedTimestamps: ActivityArray
  travelerNextStops?: Array<string | null>
  travelerPercentCompletes?: ActivityArray
  travelerSpeedsMetersPerHour: ActivityArray
  vfdCurrentPercentages: ActivityArray
  wheelSpeedMmpms: ActivityArray
}
export const deviceActivity = createModel<DeviceActivity, string>({
  getState: (state) => state.deviceActivities,

  /**
   * 'device' property is the device id
   */
  selectId: (activity) => activity.device,
})

export interface SensorEventLastEntry {
  sensorId: number
  sensorEventId: number
  deviceId: string
  sensorType: string
  output0: boolean | null
  output1: boolean | null
  output2: boolean | null
  output3: boolean | null
  output4: boolean | null
  output5: boolean | null
  output6: boolean | null
  output7: boolean | null
  currentState: string | null
  previousState: string | null
  centerPivotRun: number | null
  centerPivotDir: number | null
  centerPivotWet: number | null
  /** Speed-output position the controller is aiming for. Open loop
   * it's the commanded speed, while regulating it's where the
   * regulator is driving the output.
   *
   * Two of the four speed fields below are percents and two are
   * m/min, so each carries its unit in the name. */
  centerPivotDesiredSpeedPercent: number | null
  /** Actual travel speed from the pulsed speed sensor. Null on pivots
   * without speed feedback, and for up to one sample window after a
   * valve reposition, which is correct behavior rather than a stalled
   * sensor. */
  centerPivotMeasuredSpeedMetersPerMinute: number | null
  centerPivotEndGun: number | null
  /** 1 while the controller is actively regulating to a speed target.
   * Configuring gains alone does not set this. */
  centerPivotSpeedPid: number | null
  /** Percent actually applied to the hardware. Diverges from the
   * desired percent while a valve is travelling. */
  centerPivotAppliedSpeedPercent: number | null
  /** The commanded closed-loop target; null when not regulating. Chart
   * against the measured speed for loop performance. */
  centerPivotTargetSpeedMetersPerMinute: number | null
}

export interface LoadDeviceActivityParams {
  activeFarmId: number
  /** IDs of DeviceFunctionCalls the client is waiting on. Backend returns which are confirmed. */
  waitingDfcIds?: number[]
  /**
   * Upper bound for created_at_timestamp filter. Sent as ISO string set to
   * 24h in the future so devices with forward-skewed RTCs aren't filtered
   * out — sometimes created_at_timestamp is unreliable but we dont want to
   * filter out future device events.
   */
  maxDate?: string
}
export interface DeviceLastKnownLocation {
  deviceId: string
  gpsLocation: Geo.PointGeoJson
  timestamp: string | null
  /**
   * Pivot-arm heading in degrees (0-360, clockwise from north) derived from
   * the GPS fix and the linked CenterPivot's center. Null when the device
   * is not linked to a CenterPivot with a configured center.
   */
  pivotHeadingDegrees: number | null
}
export interface CenterPivotPlanEntry {
  id: number
  centerPivotId: number
  destinationSlotId: number | null
  destinationSlotName: string | null
  destinationAngleDegrees: number
  startAngleDegrees: number
  direction: "clockwise" | "counter_clockwise"
  targetSpeedMetersPerHour: number
  monitorActivityId: number
  /** ISO timestamp at which the device-side watchdog will auto-stop. */
  expiresAt: string
  status: "pending" | "arrived" | "stuck" | "cancelled" | "expired" | "controller_error"
  createDate: string
}

export interface LinearIrrigatorPlanEntry {
  id: number
  linearIrrigatorId: number
  destinationSlotId: number | null
  destinationSlotName: string | null
  destinationDistanceMeters: number
  startDistanceMeters: number
  direction: "forward" | "reverse"
  targetSpeedMetersPerHour: number
  monitorActivityId: number
  expiresAt: string
  status: "pending" | "arrived" | "stuck" | "cancelled" | "expired" | "controller_error"
  createDate: string
}

export interface LoadDeviceActivityResponse {
  comments: DeviceComment[]
  deviceActivities: DeviceActivity[]
  deviceEvents: DeviceEvent[]
  reelRuns: ReelRun[]
  sensorEventLasts: SensorEventLastEntry[]
  deviceLastKnownLocations: DeviceLastKnownLocation[]
  farmCacheCalculatedAt?: string | null
  farmCacheExpirationDate?: string | null
  /** Subset of waitingDfcIds now confirmed by a device event. */
  confirmedDfcIds?: number[]
  /** Active CenterPivotPlan rows (status=PENDING) for the farm's pivots. */
  activePivotPlans?: CenterPivotPlanEntry[]
  /** Active LinearIrrigatorPlan rows (status=PENDING) for the farm's linears. */
  activeLinearPlans?: LinearIrrigatorPlanEntry[]
}

export interface LoadActiveFarmResponse {
  appNotifications: NotificationEntity[] | null | undefined
  // The API ships CenterPivots in the raw flat-boundary shape; the redux
  // reducer normalizes via `prepareCenterPivot` before storing.
  centerPivots: CenterPivotPayload[] | null | undefined
  // The API ships LinearIrrigators in the raw flat-boundary shape;
  // the redux reducer normalizes via `prepareLinearIrrigator` before
  // storing.
  linearIrrigators?: LinearIrrigatorPayload[] | null | undefined
  pivotEndGunTables?: PivotEndGunTable[] | null | undefined
  deviceConfigurations: DeviceConfiguration[] | null | undefined
  deviceFarmAssociations: DeviceFarmAssociation[] | null | undefined
  deviceShareCodes: DeviceShareCode[] | null | undefined
  deviceUserAssociations: DeviceUserAssociation[] | null | undefined
  errorCode?: "NO FARM ASSOCIATION"
  farmJoinCode: FarmJoinCode | null
  fields: FarmField[] | null | undefined
  flowVolumeL24H: Array<{deviceId:string, flowVolumeLLast24H:number}> | null | undefined
  irrigationPlans: IrrigationPlan[] | null | undefined
  namedDeviceActions: NamedDeviceAction[] | null | undefined
  triggers: EventActionTrigger[] | null | undefined
  userPermissions: FarmUserPermissions | null | undefined
}

export interface SelectDevicePayload
  extends DeviceIdProps,
    CodaDeviceAliasProps {
  gpsLocation: Geo.MultiPoint | undefined
}

export type SelectDeviceHandler = (
  device: CodaDeviceAliasProps &
    DeviceIdProps & {
      gpsLocation: Geo.MultiPoint | null | undefined
    },
) => void

type SelectFieldPayload = Pick<FarmField, "centerLatLng" | "fieldId">

export type SelectFieldHandler = (field: SelectFieldPayload) => void

export interface SelectReelRunPayload extends ReelRunIdProps {
  centerLatLng: Geo.MultiPoint | undefined
}

export type SelectReelRunHandler = (runData: SelectReelRunPayload) => void
export interface FarmAccountProperties {
  address_line_1: string
  address_line_2?: string | null
  city: string
  country: string
  gpsLocation?: Geo.PointGeoJson | null
  name: string
  postalCode: string
  region: string
}

export interface FarmAccount extends FarmAccountProperties {
  accountOwnerId?: string | null
  createDate?: string
  createdByUserId?: string
  deactivateDate?: string
  deactivatedByUserId?: string | null
  id: number
  isActive?: boolean
  isDemo: boolean | null | undefined
  subscriptionRenewalDueDate?: string | null
}
